import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { IAPIService } from 'plugins/tag/plugin_data_service/ts/Application/api/IAPIService';
import { IDataServiceFeature } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/features/IDataServiceFeature';
import { IDataServiceFeatureContext } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/features/IDataServiceFeatureContext';
import { ITagConfigDataServiceConfig } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/model/config/ITagConfigDataServiceConfig';

/**
 * @copyright © Paramount Streaming
 * @description Abstract base for Data Service features. Subclass and implement
 * name, featureKey, getPublicAPI(), and install(). enabled() has a default but can be overridden.
 *
 * `name` follows the codebase convention (class name for logging/debugging).
 * `featureKey` is the config/registry identifier (e.g. 'PlaybackParamsBuilder')
 * used for config lookup and tag.API.DATA registration.
 */
export abstract class AbstractDataServiceFeature implements IDataServiceFeature {
	/**
	 * @description Class name for logging and debugging. Follows the codebase-wide convention
	 * where every class surfaces its name (e.g. 'PlaybackParamsBuilderFeature').
	 * Not used for config lookup — see {@link featureKey} for that purpose.
	 */
	abstract get name(): string;

	/**
	 * @description Feature key for config lookup and public API registration.
	 * Subclass must implement. Used by enabled() to find config.features[featureKey]
	 * and by TagDataService to register tag.API.DATA[featureKey].
	 */
	abstract get featureKey(): string;

	/**
	 * @description Default: enabled when config.features?.[this.featureKey]?.enabled !== false.
	 * Override for custom logic.
	 */
	enabled( config: ITagConfigDataServiceConfig ): boolean {
		const featureConfig = config?.features?.[ this.featureKey ];
		return featureConfig?.enabled !== false;
	}

	/**
	 * @description Default: empty object. Override to expose methods on tag.API.DATA.<featureKey>.
	 */
	getPublicAPI(): Record<string, unknown> {
		return {};
	}

	/**
	 * @description Feature context provided during install. Holds references to tag, utils, logger, and tagConfig.
	 */
	protected context: IDataServiceFeatureContext | undefined;

	/**
	 * @description Logger instance extracted from context for convenience.
	 */
	protected logger: ILogger | undefined;

	/**
	 * @description API service for fetching data via the active adapter. Set during install().
	 */
	protected apiService: IAPIService | undefined;

	/**
	 * @description Returns the API service. Throws if the feature was not installed (context not set).
	 * @returns {IAPIService}
	 */
	protected getApiService(): IAPIService {
		if ( this.apiService === undefined ) {
			throw new Error( 'AbstractDataServiceFeature: apiService not available. Ensure install(context) was called.' );
		}
		return this.apiService;
	}

	/**
	 * @description Installs the feature: sets context, logger, and apiService, then calls doInstall().
	 * Subclasses must implement doInstall() for feature-specific setup (e.g. Conveyor registration).
	 */
	install( context: IDataServiceFeatureContext ): void {
		this.context = context;
		this.logger = context.logger;
		this.apiService = context.apiService;
		this.doInstall( context );
	}

	/**
	 * @description Subclass must implement (e.g. register Conveyor transformer, event handlers).
	 */
	protected abstract doInstall( context: IDataServiceFeatureContext ): void;
}